import React, { useEffect, useRef } from 'react';
import { ContentLoader } from '@mp/components/ContentLoader';
import { areArraysEqualSorted, IdLike, normalizeIds, sortNumbers } from '@mp/utils';
import { useQueryClient } from '@tanstack/react-query';

import { Box, Form, Heading, MultiSelectField, Text, useToast } from '@medely/ui-kit';
import { useAssignmentRequestsForEHRs, useEHRExperiences, useEHRs } from './graphql';
import { useEhrMutations } from './mutations';
import { ehrSchema } from './schema';

export const ElectronicHealthRecordsView = () => {
  const { userEhrs, isLoading: isUserEhrsLoading } = useEHRExperiences();
  const { ehrs, isLoading: isEhrsLoading } = useEHRs();
  const { assignmentRequests } = useAssignmentRequestsForEHRs();
  const assignmentRequestId = assignmentRequests?.[0]?.id as number | undefined;
  const { addEHR } = useEhrMutations();
  const toast = useToast();
  const queryClient = useQueryClient();

  const ehrOptions = ehrs.map((ehr) => ({ value: Number(ehr.id), label: ehr.label || '' }));

  const initialValues = {
    ehr_systems: userEhrs.map((ehr: { id: number | string }) => Number(ehr.id)),
  };
  const isLoading = isEhrsLoading || isUserEhrsLoading;

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const invalidateTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastSavedRef = useRef<number[]>([]);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const saveEHRs = (newIds: Array<IdLike>) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const normalized = normalizeIds(newIds);
    const sortedNew = sortNumbers(normalized);
    const sortedLast = sortNumbers(lastSavedRef.current);

    if (areArraysEqualSorted(sortedNew, sortedLast)) {
      return;
    }

    saveTimeoutRef.current = setTimeout(() => {
      lastSavedRef.current = [...sortedNew];

      addEHR(
        { values: sortedNew, assignment_request_id: assignmentRequestId },
        {
          onSuccess: () => {
            if (toastTimeoutRef.current) {
              clearTimeout(toastTimeoutRef.current);
            }

            toastTimeoutRef.current = setTimeout(() => {
              toast.success('EHRs saved'),
                {
                  name: 'check',
                };
            }, 200);

            invalidateTimeoutRef.current = setTimeout(() => {
              queryClient.invalidateQueries({
                predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'ehrs',
              });
            }, 500);
          },
          onError: (_error: unknown) => {
            if (toastTimeoutRef.current) {
              clearTimeout(toastTimeoutRef.current);
            }

            toastTimeoutRef.current = setTimeout(() => {
              toast.error('Failed to save EHRs');
            }, 200);
          },
        },
      );
    }, 1000);
  };
  useEffect(() => {
    lastSavedRef.current = userEhrs.map((ehr: { id: number | string }) => Number(ehr.id));
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (invalidateTimeoutRef.current) {
        clearTimeout(invalidateTimeoutRef.current);
      }
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, [userEhrs]);

  const handleSubmit = () => {
    // No-op: auto-save is handled by formatOnChange
  };

  return (
    <Box mb={4} testId="electronic-health-records-section" py={2} bgColor="surface.paper">
      <Box mb={1}>
        <Heading size="xs">Electronic Health Records</Heading>
        <Box mt={1}>
          <Text size="l" color="text.secondary">
            Add EHR software you have experience with
          </Text>
        </Box>
      </Box>
      <ContentLoader loading={isLoading}>
        <Form
          key={initialValues.ehr_systems.join(',')}
          defaultValues={initialValues}
          schema={ehrSchema}
          handleSubmit={handleSubmit}
        >
          <MultiSelectField
            name="ehr_systems"
            label={''}
            placeholder="EHR*"
            options={ehrOptions}
            variant="default"
            presentation="bottomSheet"
            testId="electronic-health-records-multiselect"
            disabled={isLoading}
            formatOnChange={(newValue: IdLike[]) => {
              const normalized = normalizeIds(newValue);
              saveEHRs(normalized);
              return normalized;
            }}
          />
        </Form>
      </ContentLoader>
    </Box>
  );
};
